/** Pins record their anchor in person-days at this many pd per FTE-week, independent of utilization. */
const PIN_PD_PER_FTE_WEEK = 4;
const EPS = 1e-6;
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const clone = (v) => JSON.parse(JSON.stringify(v));
/**
 * Binds the scheduling engine to one project's configuration. Every function is pure:
 * plans go in, plans or computed positions come out.
 */
export function createEngine(config) {
    const DPW = config.workdaysPerWeek;
    const R = Object.fromEntries(config.resources.map(r => [r.key, r]));
    const roster = {};
    for (const r of config.resources)
        (roster[r.discipline] ??= []).push(r.key);
    const personTeams = {};
    for (const t of config.teams)
        for (const m of t.members)
            (personTeams[m] ??= []).push(t.id);
    const teamIndex = new Map(config.teams.map((t, i) => [t.id, i]));
    const prio = Object.fromEntries(config.priorities.map(p => [p.key, p]));
    const ownerRules = config.workstreamOwnerRules.map(r => ({ re: new RegExp(r.pattern, "i"), team: r.team }));
    const hintKeywords = config.disciplines.filter(d => d.hintKeywords?.length).map(d => [d.key, d.hintKeywords]);
    const start = new Date(config.startDate + "T00:00:00");
    const resource = (key) => {
        const r = R[key];
        if (!r)
            throw new Error(`Unknown resource "${key}"`);
        return r;
    };
    const defaultUtil = (key) => R[key]?.defaultUtilization ?? config.defaultUtilization;
    const utilOf = (util, key) => typeof util === "number" ? util : util?.[key] ?? defaultUtil(key);
    /** Person-days burned per workday on a single lane. */
    const rate = (key, util) => resource(key).fte * util;
    const capWeek = (key, util) => rate(key, util) * DPW;
    const offsetDays = (key) => (resource(key).offsetWeeks || 0) * DPW;
    function workstreamOwner(ws) {
        if (ws in config.workstreamOwners)
            return config.workstreamOwners[ws];
        for (const r of ownerRules)
            if (r.re.test(ws))
                return r.team;
        return config.defaultOwnerTeam;
    }
    /** Reads the people named for one discipline out of a free-text hint like "Eng: Brad, Design: split between Nix and Joel". */
    function parseIntended(text, disc) {
        if (!text)
            return { names: [], split: false };
        const t = String(text).toLowerCase();
        const split = t.includes("split") || t.includes("parallel");
        const positions = [];
        for (const [d, kws] of hintKeywords) {
            for (const k of kws) {
                const re = new RegExp("\\b" + escapeRe(k) + "[a-z ]*?(:|\\bbetween\\b)", "g");
                let m;
                while ((m = re.exec(t)))
                    positions.push([m.index, d, m.index + m[0].length]);
            }
        }
        positions.sort((a, b) => a[0] - b[0]);
        const dedup = new Map();
        for (const [s, d, e] of positions)
            if (!dedup.has(s) || e > dedup.get(s)[1])
                dedup.set(s, [d, e]);
        const pos = [...dedup.entries()].map(([s, [d, e]]) => [s, d, e]).sort((a, b) => a[0] - b[0]);
        let seg = null;
        for (let i = 0; i < pos.length; i++) {
            if (pos[i][1] === disc) {
                seg = t.slice(pos[i][2], i + 1 < pos.length ? pos[i + 1][0] : t.length);
                break;
            }
        }
        const scope = seg !== null ? seg : pos.length ? "" : t;
        const names = [];
        for (const [tok, person] of config.nameAliases) {
            if (new RegExp("\\b" + tok + "\\b").test(scope)) {
                if (roster[disc]?.includes(person) && !names.includes(person))
                    names.push(person);
            }
        }
        return { names, split };
    }
    /** Backlog rows to discipline chunks, in scheduling order. */
    function buildChunks(backlog) {
        const tasks = backlog.filter(t => !prio[t.p]?.excluded);
        const tier = (p) => prio[p]?.tier ?? Infinity;
        const rank = (p) => prio[p]?.rank ?? Infinity;
        const key = (t) => [t.s ? 0 : 1, tier(t.p), t.defer ? 1 : 0, t.d, rank(t.p), t.w.toLowerCase(), t.n.toLowerCase()];
        const sorted = [...tasks].sort((a, b) => {
            const ka = key(a), kb = key(b);
            for (let i = 0; i < ka.length; i++) {
                if (ka[i] < kb[i])
                    return -1;
                if (ka[i] > kb[i])
                    return 1;
            }
            return 0;
        });
        const chunks = [];
        const deferred = [];
        let cid = 0;
        for (const t of sorted) {
            const deferDiscs = new Set(t.deferDiscs || []);
            for (const [d, pd] of Object.entries(t.e)) {
                const c = { id: `c${cid++}`, key: `${t.n}|${d}`, task: t.n, ws: t.w, pri: t.p, disc: d, pd, isN: !t.s, intended: t.i };
                if (deferDiscs.has(d) && !t.defer)
                    deferred.push(c);
                else
                    chunks.push(c);
            }
        }
        return [...chunks, ...deferred.map(c => ({ ...c, deferred: true }))];
    }
    /** Auto-schedules a backlog: assigns every chunk to a person, honouring pins. */
    function scheduleAll(backlog, pins, util) {
        const u = (key) => utilOf(util, key);
        const chunks = buildChunks(backlog);
        const layout = Object.fromEntries(config.resources.map(r => [r.key, { free: [], pins: [], extra: [] }]));
        const load = Object.fromEntries(config.resources.map(r => [r.key, 0]));
        const endWk = (k) => (resource(k).offsetWeeks || 0) + load[k] / capWeek(k, u(k));
        const bkByName = {};
        for (const t of backlog)
            bkByName[t.n.trim().toLowerCase()] = t;
        for (const pin of Object.values(pins)) {
            const src = bkByName[pin.task.trim().toLowerCase()];
            const seg = { ...pin, pinned: true, orphan: !src, grew: false };
            layout[pin.person].pins.push(seg);
            load[pin.person] += seg.pd;
        }
        for (const k of Object.keys(layout))
            layout[k].pins.sort((a, b) => a.startPd - b.startPd);
        const pinnedPd = {};
        for (const pin of Object.values(pins))
            pinnedPd[pin.key] = (pinnedPd[pin.key] || 0) + pin.pd;
        const threshold = config.splitThresholdPd;
        for (const c of chunks) {
            const remaining = c.pd - (pinnedPd[c.key] || 0);
            if (remaining <= 0.5)
                continue; // fully placed by pins
            const eff = { ...c, pd: remaining };
            const { names, split } = parseIntended(c.intended, c.disc);
            const cands = names.length ? names : roster[c.disc] || [];
            if (!cands.length)
                continue;
            if ((split || eff.pd > threshold) && cands.length > 1) {
                const n = Math.min(cands.length, Math.max(2, Math.ceil(eff.pd / threshold)));
                const chosen = [...cands].sort((a, b) => endWk(a) - endWk(b)).slice(0, n);
                const capSum = chosen.reduce((s, p) => s + capWeek(p, u(p)), 0);
                chosen.forEach((p, i) => {
                    const share = (eff.pd * capWeek(p, u(p))) / capSum;
                    layout[p].free.push({ ...eff, id: eff.id + "_" + i, pd: share });
                    load[p] += share;
                });
            }
            else {
                const pick = [...cands].sort((a, b) => endWk(a) - endWk(b))[0];
                layout[pick].free.push(eff);
                load[pick] += eff.pd;
            }
        }
        // any key with several segs becomes a primary plus linked sub-chunks
        const byKey = {};
        for (const k of Object.keys(layout))
            for (const s of [...layout[k].free, ...layout[k].pins])
                (byKey[s.key] ??= []).push(s);
        for (const segs of Object.values(byKey)) {
            if (segs.length < 2)
                continue;
            const pinned = segs.filter(s => s.pinned);
            const primary = pinned.length
                ? pinned.reduce((a, b) => (a.startPd <= b.startPd ? a : b))
                : segs.reduce((a, b) => (a.pd >= b.pd ? a : b));
            for (const s of segs) {
                if (s === primary) {
                    s.isSub = false;
                    delete s.parent;
                }
                else {
                    s.isSub = true;
                    s.parent = primary.id;
                }
            }
        }
        return layout;
    }
    /** Workday a pin is locked to. */
    const pinAnchorDay = (key, startPd) => offsetDays(key) + (startPd / (resource(key).fte * PIN_PD_PER_FTE_WEEK)) * DPW;
    /** Inverse of pinAnchorDay, for pinning a brick where it currently sits. */
    const dayToPinPd = (key, day) => ((day - offsetDays(key)) / DPW) * (resource(key).fte * PIN_PD_PER_FTE_WEEK);
    /** Pins that overlap in time spread across parallel tracks. */
    function pinTracks(pinList, key, util) {
        const r = rate(key, util);
        const pins = [...pinList].sort((a, b) => a.startPd - b.startPd).map(p => ({ ...p, anchorDay: pinAnchorDay(key, p.startPd) }));
        const tracks = [];
        for (const p of pins) {
            const tr = tracks.find(t => t.end <= p.anchorDay + EPS);
            if (tr) {
                tr.items.push(p);
                tr.end = p.anchorDay + p.pd / r;
            }
            else
                tracks.push({ items: [p], end: p.anchorDay + p.pd / r });
        }
        return tracks.map(t => t.items);
    }
    /** Lanes a person's row renders: the flow lane, one per pin track, then the free-float lanes. */
    const laneCount = (layout, key, util) => 1 + pinTracks(layout[key]?.pins || [], key, util).length + (layout[key]?.extra || []).length;
    /**
     * Turns one person's lanes into calendar positions. Active lanes share the person's rate
     * equally, so two concurrent bricks each run at half speed.
     */
    function simPerson(key, entry, util) {
        const r = rate(key, util);
        const tracks = pinTracks(entry.pins, key, util);
        const lane0 = entry.free.map(s => ({ seg: { ...s }, anchor: null, pd: s.pd }));
        const pinLanes = tracks.map(items => items.map(p => ({ seg: { ...p }, anchor: p.anchorDay, pd: p.pd })));
        const floatLanes = (entry.extra || []).map(list => list.map(s => ({ seg: { ...s }, anchor: s.anchorDay ?? null, pd: s.pd })));
        const lanes = [lane0, ...pinLanes, ...floatLanes];
        const cursors = lanes.map(() => 0);
        const rem = lanes.map(l => l.map(x => x.pd));
        const started = lanes.map(l => l.map(() => null));
        const out = [];
        let t = offsetDays(key);
        for (let guard = 0; guard < 5000; guard++) {
            const waiting = [], active = [];
            for (let i = 0; i < lanes.length; i++) {
                if (cursors[i] >= lanes[i].length)
                    continue;
                const item = lanes[i][cursors[i]];
                if (item.anchor !== null && t < item.anchor - 1e-9)
                    waiting.push(i);
                else
                    active.push(i);
            }
            if (!active.length && !waiting.length)
                break;
            if (!active.length) {
                t = Math.min(...waiting.map(i => lanes[i][cursors[i]].anchor));
                continue;
            }
            const share = r / active.length;
            const frac = 1 / active.length;
            let dt = Infinity;
            for (const i of active)
                dt = Math.min(dt, rem[i][cursors[i]] / share);
            for (const i of waiting)
                dt = Math.min(dt, lanes[i][cursors[i]].anchor - t);
            for (const i of active) {
                const c = cursors[i];
                const item = lanes[i][c];
                if (started[i][c] === null)
                    started[i][c] = t;
                const pr = (item.prof ??= []);
                const last = pr[pr.length - 1];
                if (last && Math.abs(last[2] - frac) < EPS && Math.abs(last[1] - t) < EPS)
                    last[1] = t + dt;
                else
                    pr.push([t, t + dt, frac]);
                rem[i][c] -= dt * share;
                if (rem[i][c] <= EPS) {
                    out.push({ ...item.seg, pd: item.pd, d0: started[i][c], d1: t + dt, lane: i, prof: item.prof });
                    cursors[i]++;
                }
            }
            t += dt;
        }
        return out.sort((a, b) => a.d0 - b.d0);
    }
    /** Every person's resolved bricks. */
    function resolveAll(layout, util) {
        const out = {};
        for (const r of config.resources)
            out[r.key] = simPerson(r.key, layout[r.key] || { free: [], pins: [], extra: [] }, utilOf(util, r.key));
        return out;
    }
    /** Which team's section a person's brick renders in when they sit on several teams. */
    function laneOf(key, ws) {
        const teams = personTeams[key] || [];
        if (teams.length <= 1)
            return teams[0];
        const own = workstreamOwner(ws.trim());
        return teams.includes(own) ? own : config.homeTeam[key];
    }
    /** Per team, per milestone block: work items finishing (done) and in flight with % complete (wip). */
    function computeMilestones(resolved, maxWeeks) {
        const MW = config.milestoneWeeks;
        const agg = {};
        for (const [k, segs] of Object.entries(resolved)) {
            for (const s of segs) {
                const a = (agg[s.key] ??= { total: 0, lastDay: 0, wk: new Map(), execs: new Set(), ws: s.ws, task: s.task });
                a.total += s.pd;
                a.execs.add(k);
                a.lastDay = Math.max(a.lastDay, s.d1);
                const pdPerDay = s.pd / Math.max(0.01, s.d1 - s.d0);
                for (let w = Math.floor(s.d0 / DPW); w * DPW < s.d1; w++) {
                    const lo = Math.max(s.d0, w * DPW), hi = Math.min(s.d1, (w + 1) * DPW);
                    if (hi > lo)
                        a.wk.set(w, (a.wk.get(w) || 0) + (hi - lo) * pdPerDay);
                }
            }
        }
        const nBlocks = Math.ceil(maxWeeks / MW);
        const out = config.teams.map(() => Array.from({ length: nBlocks }, () => ({ done: [], wip: [] })));
        const reporting = config.teams.filter(t => t.milestonesFromExecutors);
        for (const a of Object.values(agg)) {
            const lastWk = Math.ceil(a.lastDay / DPW);
            const targets = new Set([workstreamOwner(a.ws.trim())]);
            for (const t of reporting) {
                if ([...a.execs].some(e => { const ts = personTeams[e] || []; return ts.length === 1 && ts[0] === t.id; }))
                    targets.add(t.id);
            }
            for (let b = 0; b < nBlocks; b++) {
                const lo = b * MW, hi = (b + 1) * MW;
                let active = false;
                for (const w of a.wk.keys())
                    if (w >= lo && w < hi) {
                        active = true;
                        break;
                    }
                if (!active)
                    continue;
                for (const tp of targets) {
                    const idx = teamIndex.get(tp);
                    if (idx === undefined)
                        continue;
                    const slot = out[idx][b];
                    if (lastWk > lo && lastWk <= hi) {
                        if (!slot.done.includes(a.task))
                            slot.done.push(a.task);
                    }
                    else if (lastWk > hi) {
                        let doneAmt = 0;
                        for (const [w, v] of a.wk)
                            if (w < hi)
                                doneAmt += v;
                        const pct = Math.max(5, Math.round((doneAmt / a.total) * 20) * 5);
                        if (!slot.wip.some(x => x[0] === a.task))
                            slot.wip.push([a.task, pct]);
                    }
                }
            }
        }
        return out;
    }
    /** Calendar date of a workday index. */
    const dayToDate = (d) => {
        const wk = Math.floor(d / DPW), dow = Math.min(DPW - 1, Math.floor(d % DPW));
        const dt = new Date(start);
        dt.setDate(dt.getDate() + wk * 7 + dow);
        return dt;
    };
    /** Workday index of a calendar date (weekends clamp to the last workday). */
    const dateToDay = (date) => {
        const cal = Math.floor((date.getTime() - start.getTime()) / 86400000);
        if (cal < 0)
            return 0;
        return Math.floor(cal / 7) * DPW + Math.min(DPW - 1, cal % 7);
    };
    /** Bricks in the same lane whose spans overlap; a healthy plan has none. */
    function findOverlaps(resolved) {
        const hits = [];
        for (const [k, segs] of Object.entries(resolved)) {
            const byLane = {};
            for (const s of segs)
                (byLane[s.lane] ??= []).push(s);
            for (const [lane, list] of Object.entries(byLane)) {
                const sorted = [...list].sort((a, b) => a.d0 - b.d0);
                for (let i = 1; i < sorted.length; i++) {
                    if (sorted[i].d0 < sorted[i - 1].d1 - EPS)
                        hits.push({ person: k, lane: +lane, a: sorted[i - 1].id, b: sorted[i].id });
                }
            }
        }
        return hits;
    }
    return {
        config, rate, capWeek, defaultUtil, utilOf, workstreamOwner, parseIntended, buildChunks, scheduleAll,
        pinTracks, pinAnchorDay, dayToPinPd, laneCount, simPerson, resolveAll, laneOf, computeMilestones,
        dayToDate, dateToDay, findOverlaps, personTeams, roster, clone,
    };
}
//# sourceMappingURL=engine.js.map